<template>
  <div class="reel" :class="{ 'reel--spinning': spinning, 'reel--hit': hit }">
    <div class="reel__stage">
      <img :src="signImg" class="reel__sign" alt="Het Priority Spel" />

      <div class="reel__window">
        <div ref="stripEl" class="reel__strip" @transitionend="onLandEnd">
          <div
            v-for="(item, i) in strip"
            :key="i"
            class="reel__segment"
            :style="{ height: ITEM_H + 'px', background: item.color }"
          >
            <!-- Figma shows a centered game image per segment; the real game art
                 arrives with the external minigames. An <img> is contained so it
                 never overflows the segment; emoji is the placeholder fallback. -->
            <img
              v-if="item.image"
              :src="item.image"
              class="reel__segment-img"
              :style="item.size ? { width: item.size + 'px', height: item.size + 'px' } : null"
              alt=""
              aria-hidden="true"
            />
            <span v-else class="reel__segment-icon">{{ item.icon }}</span>
          </div>
        </div>

        <!-- landing flash on the centre payline row (blinks the landed segment) -->
        <div
          class="reel__hit"
          :class="{ 'is-on': hit }"
          :style="{ top: ITEM_H + 'px', height: ITEM_H + 'px' }"
          aria-hidden="true"
        />
      </div>

      <img :src="drumImg" class="reel__drum" alt="" aria-hidden="true" />
      <!-- real marquee bulbs mapped onto the baked drum bulbs — they light up one
           by one around the frame while spinning (a running chase) -->
      <div class="reel__bulbs" aria-hidden="true">
        <span v-for="(s, i) in bulbStyles" :key="i" class="reel__bulb" :style="s" />
      </div>

      <img
        :src="arrowImg"
        class="reel__arrow reel__arrow--left"
        :class="{ 'is-hit': hit }"
        alt=""
        aria-hidden="true"
      />
      <img
        :src="arrowImg"
        class="reel__arrow reel__arrow--right"
        :class="{ 'is-hit': hit }"
        alt=""
        aria-hidden="true"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import drumImg from '@/assets/v3/reel-drum.webp'
import signImg from '@/assets/v3/reel-sign.webp'
import arrowImg from '@/assets/v3/reel-arrow.webp'

const props = defineProps({
  items: { type: Array, required: true }, // [{ key, label, color, icon, image? }]
})
const emit = defineEmits(['landed'])

const ITEM_H = 108 // window height = 3 × ITEM_H; matches the Figma 108px segments
const COPIES = 8
const VISIBLE = 3

// Marquee bulbs. Centres are % of the 209×372 stage, detected from the baked
// drum art and ordered clockwise from the top-left so the lit bulb travels
// around the frame. Each bulb's negative animation-delay phases the chase.
const LOOP = 1.5 // seconds for one full trip of the light around the frame
const BULBS = [
  // top row L→R
  [9.93, 5.75],
  [20.27, 5.75],
  [30.4, 5.75],
  [39.81, 5.75],
  [49.75, 5.75],
  [59.59, 5.75],
  [69.62, 5.75],
  [79.68, 5.75],
  [89.85, 5.75],
  // right column T→B
  [91.16, 12.25],
  [91.16, 18.53],
  [91.16, 24.85],
  [91.16, 31.14],
  [91.16, 37.38],
  [91.16, 43.62],
  [91.16, 49.9],
  [91.16, 56.18],
  [91.16, 62.43],
  [91.16, 68.71],
  [91.16, 74.96],
  [91.16, 81.22],
  [91.16, 87.36],
  // bottom row R→L
  [89.94, 93.55],
  [79.66, 93.55],
  [69.71, 93.55],
  [59.64, 93.55],
  [49.77, 93.55],
  [40.27, 93.55],
  [30.18, 93.55],
  [20.15, 93.55],
  [9.91, 93.55],
  // left column B→T
  [8.62, 87.36],
  [8.62, 81.22],
  [8.62, 74.96],
  [8.62, 68.71],
  [8.62, 62.43],
  [8.62, 56.18],
  [8.62, 49.9],
  [8.62, 43.62],
  [8.62, 37.38],
  [8.62, 31.14],
  [8.62, 24.85],
  [8.62, 18.53],
  [8.62, 12.25],
]
const bulbStyles = BULBS.map((b, i) => ({
  left: b[0] + '%',
  top: b[1] + '%',
  // negative delay by reverse index → the lit bulb runs clockwise, immediately
  animationDelay: (-((BULBS.length - i) / BULBS.length) * LOOP).toFixed(3) + 's',
}))

const strip = computed(() => {
  const out = []
  for (let c = 0; c < COPIES; c++) out.push(...props.items)
  return out
})

const n = props.items.length
const stripEl = ref(null)
const spinning = ref(false) // reactive: drives the bulb-sweep + drum dim
const hit = ref(false) // reactive: drives the landing blink + arrow pulse

// Rest with the payline on the boundary between items[0] and items[1] so two games
// straddle the arrows (Figma hero: rocket above, soccer below) — shift up half a segment.
const REST = ITEM_H * (1 - n) - ITEM_H / 2

// --- Two-phase spin ---------------------------------------------------------
// The reel starts spinning the instant the button is pressed (startSpin) and only
// lands once the server outcome arrives (landOn). This decouples the reel from
// network latency, so the click feels instant and a slow server is masked by the
// loop. Motion is driven by the compositor (a seamless CSS keyframe loop + a CSS
// transition for the land), NOT requestAnimationFrame — so it can't get starved,
// and a setTimeout safety net guarantees the land (and thus the 'landed' emit)
// completes even if 'transitionend' never fires.
const SEG_PER_S = 18 // loop speed, segments per second
const LOOP_CYCLE_MS = Math.round((n / SEG_PER_S) * 1000) // time for one full n-item cycle

// Spin-up: a short ease-in so the reel doesn't jump straight to full speed. The
// curve's END slope is 2.4× its average, so the distance below exits the ramp at
// exactly the loop speed and the hand-off into the loop is invisible.
const ACCEL_MS = 280
const ACCEL_EASE = 'cubic-bezier(0.5, 0, 0.75, 0.4)'
const ACCEL_SEG = (SEG_PER_S * (ACCEL_MS / 1000)) / 2.4

// The land is deliberately long and varies per spin, so it is never obvious how
// soon the reel will settle. MIN_SPIN keeps the loop running even when the server
// answers instantly.
const MIN_SPIN_MS = [1000, 1600] // free-spin at least this long, randomised per spin
const LAND_MS = [1900, 3000] // deceleration time, randomised per spin
// easeOutCubic starts at 3.03× its average speed, so a land of `ms` needs this
// many segments of runway to leave the loop without a visible jolt in speed.
const LAND_EASE = 'cubic-bezier(0.33, 1, 0.68, 1)'
const LAND_EASE_SLOPE = 3.03
const runwayFor = (ms) => Math.max(6, Math.round((SEG_PER_S * (ms / 1000)) / LAND_EASE_SLOPE))
// …then it overshoots a few px and clicks back, the way a real reel does.
const OVERSHOOT_PX = [8, 15]
const SETTLE_MS = 420
const SETTLE_EASE = 'cubic-bezier(0.22, 1, 0.36, 1)'
// how long the landed segment stays lit before the host moves on
const CELEBRATE_MS = 2000

const rand = (lo, hi) => lo + Math.random() * (hi - lo)

const reduceMotion =
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches

let phase = 'idle' // idle | accel | loop | land | settle
let curOffset = REST // last committed strip translateY (px)
let pendingKey = null // outcome to land on (null until the server replies)
let pendingIdx = -1
let landEmits = true // whether finishing the land emits 'landed' (false for a stopSpin)
let spinStart = 0
let minSpinMs = MIN_SPIN_MS[0]
let landTarget = 0 // final offset of the current land (the settle returns to it)
let accelTimer = null
let minSpinTimer = null
let landEndTimer = null
let hitTimer = null

const paylineIndex = (o) => 1 - o / ITEM_H // strip index on the payline for offset o
const offsetForIndex = (i) => ITEM_H * (1 - i)

// Read the strip's live translateY (mid-animation this is the current position).
function currentTranslateY() {
  const el = stripEl.value
  if (!el) return curOffset
  const m = getComputedStyle(el).transform.match(/matrix\(([^)]+)\)/)
  return m ? parseFloat(m[1].split(',')[5]) : curOffset
}

// Park the strip at a fixed offset with no animation/transition running.
function park(px) {
  const el = stripEl.value
  if (el) {
    el.style.animation = 'none'
    el.style.transition = 'none'
    el.style.transform = `translateY(${px}px)`
  }
  curOffset = px
}

onMounted(() => park(REST))

// Begin a free-spinning loop immediately (no target yet). Call landOn(key) once
// the server outcome is known, or stopSpin() if the spin request failed.
function startSpin() {
  if (spinning.value) return
  clearTimers()
  hit.value = false
  pendingKey = null
  pendingIdx = -1
  landEmits = true
  spinning.value = true
  minSpinMs = rand(MIN_SPIN_MS[0], MIN_SPIN_MS[1])
  if (reduceMotion) {
    phase = 'idle' // reduced motion: don't loop; landOn/stopSpin settle directly
    return
  }
  phase = 'accel'
  spinStart = performance.now()
  const el = stripEl.value
  const to = curOffset - ACCEL_SEG * ITEM_H
  if (el) {
    el.style.animation = 'none'
    el.style.transition = 'none'
    el.style.transform = `translateY(${curOffset}px)`
    void el.offsetHeight // reflow so the ramp transitions FROM the parked position
    el.style.transition = `transform ${ACCEL_MS}ms ${ACCEL_EASE}`
    el.style.transform = `translateY(${to}px)`
  }
  curOffset = to
  accelTimer = setTimeout(() => startLoop(to), ACCEL_MS)
}

// Seamless constant-speed loop: one iteration moves exactly one n-item cycle, so
// the wrap is invisible and the compositor runs it with no per-frame JS.
function startLoop(from) {
  if (phase !== 'accel') return
  phase = 'loop'
  const el = stripEl.value
  if (el) {
    el.style.transition = 'none'
    el.style.setProperty('--loop-from', `${from}px`)
    el.style.setProperty('--loop-to', `${from - n * ITEM_H}px`)
    el.style.animation = `reel-loop ${LOOP_CYCLE_MS}ms linear infinite`
  }
}

// Land on the segment for `key`. Respects MIN_SPIN_MS: if the server replied
// faster than that, the loop keeps running until the minimum spin time elapses.
function landOn(key) {
  const idx = props.items.findIndex((it) => it.key === key)
  if (idx < 0) return stopSpin() // unknown outcome → just stop; never fake a landing
  pendingKey = key
  pendingIdx = idx
  if (reduceMotion || phase === 'idle') return settleNow(idx, true)
  if (phase !== 'accel' && phase !== 'loop') return
  const wait = Math.max(0, minSpinMs - (performance.now() - spinStart))
  clearTimeout(minSpinTimer)
  minSpinTimer = setTimeout(() => beginLand(idx, true), wait)
}

// Graceful stop with no result (e.g. the spin request failed): decelerate onto the
// nearest segment and do NOT emit 'landed'.
function stopSpin() {
  if (!spinning.value) return
  clearTimeout(minSpinTimer)
  if (reduceMotion || (phase !== 'loop' && phase !== 'accel')) return finalizeIdle()
  beginLand(((Math.round(paylineIndex(currentTranslateY())) % n) + n) % n, false)
}

// Hand off from the loop into a decelerating CSS transition that ends on item `idx`.
// Duration and runway are randomised per spin, so one spin creeps to a halt and the
// next drops in quickly — you can never tell from the start how long it will take.
function beginLand(idx, emitLanded) {
  if (phase === 'land' || phase === 'settle') return
  phase = 'land'
  landEmits = emitLanded
  clearTimeout(accelTimer)
  const landMs = Math.round(rand(LAND_MS[0], LAND_MS[1]))
  const sweep = runwayFor(landMs)
  const el = stripEl.value
  const cur = currentTranslateY()
  const paylineNow = paylineIndex(cur)
  // nearest strip index that shows item `idx`, a pleasant sweep ahead and in range
  let endIndex = idx + n * Math.max(1, Math.round((paylineNow + sweep - idx) / n))
  while (endIndex <= paylineNow + n / 2) endIndex += n
  while (endIndex > COPIES * n - 2) endIndex -= n
  const target = offsetForIndex(endIndex)
  const overshoot = rand(OVERSHOOT_PX[0], OVERSHOOT_PX[1])
  landTarget = target
  if (el) {
    el.style.animation = 'none' // end the loop
    el.style.transition = 'none'
    el.style.transform = `translateY(${cur}px)` // freeze at the current animated position
    void el.offsetHeight // reflow so the land transitions FROM the frozen position
    el.style.transition = `transform ${landMs}ms ${LAND_EASE}`
    el.style.transform = `translateY(${target - overshoot}px)`
  }
  curOffset = target
  // transitionend advances to the settle; the timer guarantees it even if the page
  // isn't painting and transitionend never fires.
  clearTimeout(landEndTimer)
  landEndTimer = setTimeout(beginSettle, landMs + 260)
}

// The few px of overshoot click back into place — the reel "seats" on the segment.
function beginSettle() {
  if (phase !== 'land') return
  phase = 'settle'
  clearTimeout(landEndTimer)
  const el = stripEl.value
  if (el) {
    el.style.transition = `transform ${SETTLE_MS}ms ${SETTLE_EASE}`
    el.style.transform = `translateY(${landTarget}px)`
  }
  landEndTimer = setTimeout(finishLand, SETTLE_MS + 160)
}

function onLandEnd(e) {
  if (phase !== 'land' && phase !== 'settle') return
  if (e && e.target !== stripEl.value) return
  if (e && e.propertyName && e.propertyName !== 'transform') return
  if (phase === 'land') return beginSettle()
  finishLand()
}

function finishLand() {
  if (phase !== 'land' && phase !== 'settle') return
  clearTimeout(landEndTimer)
  phase = 'idle'
  const key = pendingKey
  const idx = pendingIdx >= 0 ? pendingIdx : 0
  park(offsetForIndex(n + idx)) // re-home to a canonical copy (same visual, avoids drift)
  spinning.value = false
  if (landEmits) {
    // celebrate the landing: blink the centred segment + pulse the arrows
    hit.value = true
    clearTimeout(hitTimer)
    hitTimer = setTimeout(() => {
      hit.value = false
    }, CELEBRATE_MS)
    emit('landed', key)
  }
  pendingKey = null
  pendingIdx = -1
  landEmits = true
}

// Reduced motion, or a land with no active loop: settle straight onto the result
// and keep the flow going, with no spin animation.
function settleNow(idx, doEmit) {
  finalizeIdle()
  park(offsetForIndex(n + idx))
  if (doEmit) {
    const key = props.items[idx]?.key
    // next tick, so a caller that just started the spin doesn't re-enter synchronously
    clearTimeout(hitTimer)
    hitTimer = setTimeout(() => emit('landed', key), 120)
  }
}

function finalizeIdle() {
  clearTimers()
  phase = 'idle'
  spinning.value = false
  const el = stripEl.value
  if (el) el.style.animation = 'none'
}

function clearTimers() {
  clearTimeout(accelTimer)
  clearTimeout(minSpinTimer)
  clearTimeout(landEndTimer)
}

onBeforeUnmount(() => {
  clearTimers()
  clearTimeout(hitTimer)
})

defineExpose({ startSpin, landOn, stopSpin })

// expose VISIBLE just to keep it referenced/tunable
void VISIBLE
</script>

<style lang="scss" scoped>
.reel {
  display: flex;
  justify-content: center;
}

.reel__stage {
  position: relative;
  width: 209px; // Figma drum 209×372
  height: 372px;
}

.reel__sign {
  position: absolute;
  top: -50px; // sits so its base covers the top-sliver segment art (clean top like Figma)
  left: 50%;
  transform: translateX(-50%);
  width: 163px;
  height: auto;
  z-index: 5;
  filter: drop-shadow(0 4px 10px rgba(0, 0, 0, 0.35));
  pointer-events: none;
}

.reel__window {
  position: absolute;
  // Figma drum inner opening = 175×325 within the 209×372 drum (insets ~17/23).
  // 3 × ITEM_H (108) = 324; segments reach close to the gold bulb frame.
  top: 24px;
  bottom: 24px;
  left: 17px;
  right: 17px;
  overflow: hidden;
  border-radius: 5px; // the drum's inner opening has near-square corners
  background: var(--c-seg-navy);
  z-index: 1;
}

.reel__strip {
  will-change: transform;
}

.reel__segment {
  display: flex;
  align-items: center;
  justify-content: center;
  // Art is sized per-segment (item.size) to the Figma art box, centred here.
  // Figma 426:295–299: strong glossy white highlight from the top of each segment
  // (spec inset 0 4px 21.1px 8px rgba(255,255,255,0.97/0.67); uniform here for the reel).
  box-shadow: inset 0 4px 20px 4px rgba(255, 255, 255, 0.7);
}

.reel__segment-icon {
  font-size: 46px;
  line-height: 1;
  filter: drop-shadow(0 3px 5px rgba(0, 0, 0, 0.4));
}

.reel__segment-img {
  // sized per-segment from the Figma art box (item.size); contain preserves aspect
  display: block;
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  filter: drop-shadow(0 3px 5px rgba(0, 0, 0, 0.4));
}

// Landing flash on the payline row. It covers the WHOLE segment (a flat screen-
// blended white plus a bright inner rim and an outer halo), so the segment itself
// lights up instead of a small glow hovering over its middle.
.reel__hit {
  position: absolute;
  left: 0;
  right: 0;
  z-index: 2;
  pointer-events: none;
  opacity: 0;
  background: #fff;
  mix-blend-mode: screen;
  box-shadow:
    inset 0 0 26px 10px rgba(255, 255, 255, 0.95),
    0 0 30px 12px rgba(255, 224, 150, 0.75);
}
.reel__hit.is-on {
  animation: hit-blink 2s var(--ease-in-out) both;
}
// Eased pulses that fade out rather than a hard on/off flicker, and long enough
// to read which segment you landed on.
@keyframes hit-blink {
  0% {
    opacity: 0;
  }
  7% {
    opacity: 0.92;
  }
  20% {
    opacity: 0.22;
  }
  32% {
    opacity: 0.85;
  }
  46% {
    opacity: 0.2;
  }
  58% {
    opacity: 0.72;
  }
  72% {
    opacity: 0.24;
  }
  84% {
    opacity: 0.5;
  }
  100% {
    opacity: 0;
  }
}

.reel__drum {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  z-index: 3;
  pointer-events: none;
  // the drum art already has a transparent interior, so it composites normally;
  // this keeps the gold frame opaque so the "spellen" badge can tuck behind it.
  transition: filter 0.45s var(--ease-out);
}
// While spinning, take the baked (always-lit) bulbs down a little so the running
// chase reads as bulbs switching on one by one. Only a little: dimming the frame
// hard made the whole reel look switched off rather than animated.
.reel--spinning .reel__drum {
  filter: brightness(0.84) saturate(1.04);
}

// real marquee bulbs overlaid on the baked drum bulbs. Off = opacity 0 (the
// baked gold bulb shows through); a screen-blended white bloom lights each bulb
// in turn as the chase travels the frame while spinning.
.reel__bulbs {
  position: absolute;
  inset: 0;
  z-index: 3;
  pointer-events: none;
}
.reel__bulb {
  position: absolute;
  width: 12px;
  height: 12px;
  transform: translate(-50%, -50%);
  border-radius: 50%;
  background: radial-gradient(circle, #ffffff 0%, #ffe9a8 48%, rgba(255, 200, 90, 0) 74%);
  // a soft bloom — enough to read as "lit", not a hard white dot
  box-shadow:
    0 0 7px 2px rgba(255, 240, 195, 0.75),
    0 0 16px 6px rgba(255, 205, 110, 0.45);
  opacity: 0;
  will-change: opacity;
}
.reel--spinning .reel__bulb {
  animation: bulb-chase 1.5s ease-out infinite;
}
// on the landing the chase stops and the whole marquee flashes with the segment
.reel--hit .reel__bulb {
  animation: bulb-celebrate 2s var(--ease-in-out) both;
  animation-delay: 0s !important; // the chase's per-bulb phase must not stagger this
}
@keyframes bulb-celebrate {
  0%,
  20%,
  46%,
  72%,
  100% {
    opacity: 0;
  }
  7%,
  32%,
  58% {
    opacity: 1;
  }
}
// one lit bulb with a soft trailing tail running around the ring — it fades in
// and out rather than snapping on, so the chase reads as a glow, not a strobe
@keyframes bulb-chase {
  0% {
    opacity: 0;
  }
  6% {
    opacity: 0.9;
  }
  20% {
    opacity: 0.3;
  }
  38% {
    opacity: 0;
  }
  100% {
    opacity: 0;
  }
}

.reel__arrow {
  position: absolute;
  top: 50%;
  // source art is a square with ~28% transparent padding, so the box is scaled
  // up to land the visible arrow near the Figma 56×59 and overlap the segments
  width: 74px;
  height: 74px;
  z-index: 4;
  pointer-events: none;

  &--left {
    left: -14px; // overhangs the gold frame edge (Figma arrow tail on the red bg)
    transform: translateY(-50%) scaleX(-1);
    &.is-hit {
      animation: arrow-pulse-left 2s var(--ease-out) both;
    }
  }
  &--right {
    right: -14px; // overhangs the gold frame edge (Figma arrow tail on the red bg)
    transform: translateY(-50%);
    &.is-hit {
      animation: arrow-pulse-right 2s var(--ease-out) both;
    }
  }
}
// Three eased pulses, in step with the segment flash, each a little smaller than
// the last so the celebration settles instead of stopping dead.
@keyframes arrow-pulse-left {
  0%,
  20%,
  46%,
  72%,
  100% {
    transform: translateY(-50%) scaleX(-1) scale(1);
  }
  7% {
    transform: translateY(-50%) scaleX(-1) scale(1.42);
  }
  32% {
    transform: translateY(-50%) scaleX(-1) scale(1.3);
  }
  58% {
    transform: translateY(-50%) scaleX(-1) scale(1.18);
  }
}
@keyframes arrow-pulse-right {
  0%,
  20%,
  46%,
  72%,
  100% {
    transform: translateY(-50%) scale(1);
  }
  7% {
    transform: translateY(-50%) scale(1.42);
  }
  32% {
    transform: translateY(-50%) scale(1.3);
  }
  58% {
    transform: translateY(-50%) scale(1.18);
  }
}
</style>

<!-- Non-scoped: the reel loop is started imperatively via el.style.animation in
     the script, so its @keyframes name must NOT be scope-hashed. One iteration
     moves exactly one n-item cycle (set as --loop-from/--loop-to on the element),
     so the wrap is seamless and the loop runs entirely on the compositor. -->
<style>
@keyframes reel-loop {
  from {
    transform: translateY(var(--loop-from));
  }
  to {
    transform: translateY(var(--loop-to));
  }
}
</style>
